<template>
  <!-- Row: Chart -->
  <div class="row f-row" style="height: calc(100% - 36px)!important;">
    <div class="col col-12 text-center progress-div" v-if="showLoading">
      <QSLoading :isLoading="showLoading" :size="35" :colorTheme="colorTheme" />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="chartData.length === 0"
      style="position: absolute; left: 0; top: 47.5%;"
    >
      {{ zeroRequests ? "Get data" : "No Data Available" }}
    </div>
    <div class="col col-12" style="height:100%; width:100%">
      <div
        class="h-100 d-flex flex-column"
        id="bars_detail-div"
        style="width: 100%"
      >
        <svg id="svg-bars_detail"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverDetailPortfolio } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorExpo,
  colorFwdF,
  colorHedge,
  darkFwdF,
  formatNumber,
  formatPercentSm,
  numberFormat,
} from "@/_core/_helpers/_parametric_tools";
import { select } from "d3";
import QSLoading from "@/components/QSLoading.vue";
import { capitalizeFirst } from "../../_core/_helpers/_helpers";

export default {
  name: "CommodityPortfolioDetailChart",
  props: {
    chartData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    fixing_type: { required: false, default: "basis" },
    hedge: { required: true, default: 0 },
    processData: { required: true },
    program: { required: true, default: 0 },
    renderChartSignal: { required: true },
    showExpo: { required: true },
    showBasisRatio: { required: true },
    showHedge: { required: true },
    showLoading: { required: true },
    showProgram: { required: true },
    showTooltip: { required: true },
    units: { required: true, default: "" },
    zeroRequests: { required: true, type: Boolean, default: true },
  },
  components: { QSLoading },
  data() {
    return {
      capitalizeFirst: capitalizeFirst,
      D3Chart: null,
      minMaxYAxis: [Infinity, -Infinity],
      svgNode: null,
    };
  },
  created() {
    this.D3Chart = new D3Chart("bars_detail-div", "svg-bars_detail");
    this.D3HoverDetailPortfolio = new D3HoverDetailPortfolio();
  },
  mounted() {
    if (this.chartData.length > 0) {
      this.createBarChart();
    }
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    renderChartSignal() {
      if (this.chartData.length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    showLoading(val) {
      if (this.chartData.length > 0 && !val) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    chartData() {
      if (this.chartData.length > 0 && !this.showLoading) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    showExpo() {
      this.callWidth();
    },
    showBasisRatio() {
      this.callWidth();
    },
    showHedge() {
      this.callWidth();
    },
    showProgram() {
      this.callWidth();
    },
    showTooltip() {
      this.callWidth();
    },
  },
  methods: {
    deleteChart() {
      select("#bars_detail-div")
        .selectAll(".tooltip")
        .remove();
      select("#bars_detail-div")
        .select("svg > *")
        .remove();
    },
    createBarChart() {
      let accum = 0;
      this.chartData.forEach(function(d, i) {
        d.id = i;
      });
      let _this = this;
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        10,
        0.15,
        6000,
        0.5,
        20,
        0.3,
        130,
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      // this.minMaxYAxis = this.processData.minMax(this.chartData, "x", "x");
      // this.minMaxYAxis = [
      //   Math.min(0, this.minMaxYAxis[0]),
      //   Math.max(0, this.minMaxYAxis[1])
      // ];
      this.minMaxYAxis = [
        Math.min(
          0,
          this.hedge,
          this.program,
          this.chartData[7].x,
          this.chartData[8].x,
        ),
        Math.max(
          0,
          this.hedge,
          this.program,
          this.chartData[7].x,
          this.chartData[8].x,
        ),
      ];
      let del = 0.05 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      let del1 = this.minMaxYAxis[0] === 0 ? 0 : del;
      let del2 = this.minMaxYAxis[1] === 0 ? 0 : del;
      this.minMaxYAxis = [
        Math.min(0, this.minMaxYAxis[0] - del1),
        Math.max(0, this.minMaxYAxis[1] + del2),
      ];
      // === set axis
      this.svgNode = this.D3Chart.setAxisLinBand(
        this.svgNode,
        this.chartData.map((d) => d.y),
        this.chartData.map((d) => d.y),
        true,
        this.minMaxYAxis,
        4,
        true,
        numberFormat,
        0.65,
        false,
        0,
      );
      // === custom chart parts
      var barWidth = this.svgNode.y.bandwidth(); // from scale
      // include group background style
      var bkgrnd = this.svgNode
        .selectAll(".bkgrnd")
        .data([this.chartData])
        .enter()
        .append("g")
        .attr("class", "bkgrnd");
      bkgrnd
        .selectAll("rect") // background color for each time bucket
        .data(function(d) {
          return d;
        })
        .enter()
        .append("rect")
        .attr("y", function(d) {
          return _this.svgNode.y(d.y);
        })
        .attr("x", 1)
        .attr("height", barWidth)
        .attr("width", this.svgNode.width)
        .classed("rise", function(d, i) {
          return i % 2 === 1;
        })
        .classed("fall", function(d, i) {
          return Math.abs(i % 2) === 0;
        });

      // add the horizontal line that divides the analyses
      this.svgNode
        .append("line")
        .attr("class", "dividerLine")
        .attr("x1", 0)
        .attr("y1", this.svgNode.y(this.chartData[6].y))
        .attr("x2", this.svgNode.width)
        .attr("y2", this.svgNode.y(this.chartData[6].y));
      this.svgNode
        .append("line")
        .attr("class", "dividerLine")
        .attr("x1", 0)
        .attr("y1", this.svgNode.y(this.chartData[3].y))
        .attr("x2", this.svgNode.width)
        .attr("y2", this.svgNode.y(this.chartData[3].y));

      // add the y-axis line that crosses at x = 0
      this.svgNode
        .append("line")
        .attr("class", "xAxisLine")
        .attr("x1", this.svgNode.x(0) + 0.5)
        .attr("y1", 0)
        .attr("x2", this.svgNode.x(0) + 0.5)
        .attr("y2", this.svgNode.height);

      let eData = this.chartData.slice(-2),
        data = [],
        basisData = [];
      accum = 0;
      data.push({
        id: 8,
        y: eData[1].y,
        x: Math.min(0, eData[1].x),
        w: Math.max(0.000001, Math.abs(eData[1].x)),
      });
      accum = 0;
      basisData.push({
        id: 7,
        y: eData[0].y,
        x: Math.min(accum, eData[0].x + accum),
        w: Math.max(0.000001, Math.abs(eData[0].x)),
      });

      accum = 0;
      let hData = this.chartData.slice(0, -2),
        hedgeData = [],
        partData = [];
      hedgeData.push({
        id: 6,
        y: hData[6].y,
        x: Math.min(0, hData[6].x),
        w: Math.max(0.000001, Math.abs(hData[6].x)),
      });
      accum += hData[6].x;
      hedgeData.push({
        id: 5,
        y: hData[5].y,
        x: Math.min(accum, hData[5].x + accum),
        w: Math.max(0.000001, Math.abs(hData[5].x)),
      });
      accum += hData[5].x;
      hedgeData.push({
        id: 4,
        y: hData[4].y,
        x: Math.min(accum, hData[4].x + accum),
        w: Math.max(0.000001, Math.abs(hData[4].x)),
      });

      accum = 0;
      partData.push({
        id: 3,
        y: hData[3].y,
        x: Math.min(0, hData[3].x),
        w: Math.max(0.000001, Math.abs(hData[3].x)),
      });
      accum += hData[3].x;
      partData.push({
        id: 2,
        y: hData[2].y,
        x: Math.min(accum, hData[2].x + accum),
        w: Math.max(0.000001, Math.abs(hData[2].x)),
      });
      accum += hData[2].x;
      partData.push({
        id: 1,
        y: hData[1].y,
        x: Math.min(accum, hData[1].x + accum),
        w: Math.max(0.000001, Math.abs(hData[1].x)),
      });
      accum += hData[1].x;
      partData.push({
        id: 0,
        y: hData[0].y,
        x: Math.min(this.hedge, hData[0].x + this.hedge),
        w: Math.max(0.000001, Math.abs(hData[0].x)),
      });
      // === expo bars ===
      if (this.showExpo) {
        this.svgNode = this.D3Chart.hBars(
          this.svgNode,
          data,
          0.5,
          "EXPO",
          colorExpo(this.colorTheme),
          colorExpo(this.colorTheme),
          0.7,
          "x",
          "y",
          true,
          false,
          false,
          data.map((d) => d.w),
          true,
          1,
          true,
          "id",
        );
      }
      if (this.showBasisRatio) {
        this.svgNode = this.D3Chart.hBars(
          this.svgNode,
          basisData,
          0.5,
          "BASIS",
          colorHedge(this.colorTheme),
          colorHedge(this.colorTheme),
          0.7,
          "x",
          "y",
          true,
          false,
          false,
          basisData.map((d) => d.w),
          true,
          1,
          true,
          "id",
        );
      }
      // === hedge bars ===
      if (this.showHedge) {
        this.svgNode = this.D3Chart.hBars(
          this.svgNode,
          hedgeData,
          0.5,
          "HEDGE",
          colorFwdF(this.colorTheme),
          colorFwdF(this.colorTheme),
          0.6,
          "x",
          "y",
          true,
          false,
          false,
          hedgeData.map((d) => d.w),
          true,
          1,
          true,
          "id",
        );
        // === participation bars ===
        this.svgNode = this.D3Chart.hBars(
          this.svgNode,
          partData,
          0.5,
          "PART",
          darkFwdF(this.colorTheme),
          darkFwdF(this.colorTheme),
          0.65,
          "x",
          "y",
          true,
          false,
          false,
          partData.map((d) => d.w),
          true,
          1,
          true,
          "id",
        );
      }
      // add the expo, hedge and strategy lines
      if (this.showExpo) {
        this.svgNode
          .append("line")
          .attr("class", "expoLine")
          .attr("y1", 0)
          .attr("x1", this.svgNode.x(this.chartData[8].x))
          .attr("y2", this.svgNode.height)
          .attr("x2", this.svgNode.x(this.chartData[8].x));
      }
      if (this.showHedge) {
        this.svgNode
          .append("line")
          .attr("class", "hedgeLine")
          .attr("y1", 0)
          .attr("x1", this.svgNode.x(this.hedge))
          .attr("y2", this.svgNode.height)
          .attr("x2", this.svgNode.x(this.hedge));
      }
      if (this.showProgram) {
        this.svgNode
          .append("line")
          .attr("class", "strategyLine")
          .attr("y1", 0)
          .attr("x1", this.svgNode.x(this.program))
          .attr("y2", this.svgNode.height)
          .attr("x2", this.svgNode.x(this.program));
      }
      // === hover fx ===
      this.D3HoverDetailPortfolio.hoverFct(
        this.svgNode,
        this.chartData,
        "DETAIL",
        formatNumber,
        formatPercentSm,
        this.units,
        this.hedge,
        this.program,
        true,
        numberFormat,
        colorFwdF(this.colorTheme),
        colorExpo(this.colorTheme),
        "commodities",
        this.colorTheme,
        "Committed " + this.capitalizeFirst(this.fixing_type),
      );
    },
    callWidth() {
      if (this.chartData.length > 0) {
        this.createBarChart();
      }
    },
  },
};
</script>

<style scoped></style>
